import { useDocEngine } from "@hooks";
import { Device, Document, TaskDefinition, TaskState } from "@models";
import { Accordion, AccordionSummary } from "@mui/material";
import { TemplateConfig } from "@types";
import { getDocumentContexts, getDocumentTaskStatus } from "@utils";
import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { TaskAccordionCard } from "src/components/Roadmap/Task/TaskAccordionCard";
import { User } from "src/stores/models/user";

type Props = {
  deviceId: string;
  task: TaskDefinition;
  documents: Document[];
  user: User;
  device: Device;
  templatesConfig: TemplateConfig;
  tasks: TaskDefinition[];
  taskStates?: TaskState[];
};

export const TaskAccordion = ({
  task,
  documents,
  user,
  deviceId,
  device,
  templatesConfig,
  tasks,
  taskStates,
}: Props) => {
  const { orgId = "" } = useParams();
  const docEngine = useDocEngine(deviceId);
  const [isLoading, setIsLoading] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);

  // Find the task state for this task by taskKey
  const taskState = useMemo(() => {
    return taskStates?.find((state) => state.taskKey === task.taskKey);
  }, [taskStates, task.taskKey]);

  const taskType = task.taskConfig.taskType;

  const handleTaskClick = async (task: TaskDefinition) => {
    // For MANUAL tasks, the click is handled by TaskAccordionCard (expand/collapse)
    if (taskType === "MANUAL") {
      return;
    }

    setIsLoading(true);

    await docEngine.navigateToWizardOrQMS({
      templateKey: task.taskKey,
    });

    setIsLoading(false);
  };

  // For DOCUMENT tasks, check if every document that is mentioned inside the context config
  // in the TemplateConfig is present in the documents array and marked as completed.
  // MANUAL tasks don't require a template config entry.
  let templateType: string | undefined;
  if (templatesConfig[task.taskKey]) templateType = task.taskKey;

  // For DOCUMENT tasks, we require a valid template config
  // For MANUAL tasks, we don't need one
  if (!templateType && taskType !== "MANUAL") return null;

  const { incompleteTemplates } = templateType
    ? getDocumentContexts({
        templateKey: templateType,
        documents,
        user,
        orgId,
        templatesConfig,
      })
    : { incompleteTemplates: [] };

  let docStatus = templateType
    ? getDocumentTaskStatus({
        documents,
        templateKey: templateType,
        user,
        orgId,
      })
    : undefined;

  return (
    <div key={task.title} className="flex flex-col gap-x-1 items-center">
      <Accordion
        sx={{
          width: "100%",
          "&.MuiAccordion-root": {
            boxShadow: "none",
            margin: "0",
          },
          // Target all button bases except MenuItems (which are in the assignee dropdown)
          "& .MuiButtonBase-root:not(.MuiMenuItem-root)": {
            padding: "0",
            minHeight: "unset",
            "& .MuiAccordionSummary-content": {
              margin: "0",
            },
            "&.Mui-expanded": {
              minHeight: "unset",
            },
          },
        }}
        ref={accordionRef}
      >
        <AccordionSummary aria-controls="panel1-content" id="panel1-header">
          <TaskAccordionCard
            onClick={() => handleTaskClick(task)}
            task={task}
            docStatus={docStatus}
            isLoading={isLoading}
            incompleteTemplates={incompleteTemplates}
            device={device}
            templatesConfig={templatesConfig}
            user={user}
            tasks={tasks}
            taskInstance={taskState}
          />
        </AccordionSummary>
      </Accordion>
    </div>
  );
};
